import {Alert, type AlertTypes, type ButtonProps} from 'platform/components';

import {isNil, isNotNil, reject} from 'ramda';

import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath} from 'shared/web';

import {
  getPkwEnvkvIssueLabels,
  getPkwEnvkvVehicleEditTab,
  isPkwEnvkvCalculatedValueIssue,
} from '../../utils/getPkwEnvkvIssueLabels';
import {getPkwEnvkvRequirementReasonLabel} from '../../utils/getPkwEnvkvRequirementReasonLabel';

export interface PkwEnvkvAdvertisingFeedbackProps {
  type?: AlertTypes;
  vehicleId: string;
  variant: 'warning' | 'blocked';
  issueCodes: string[];
  /** The backend `envkvRequiredReasonCodes`, explaining why the vehicle needs EnVKV data. */
  requirementReasonCodes?: string[];
  onNavigate?: () => void;
}

export function PkwEnvkvAdvertisingFeedback(props: PkwEnvkvAdvertisingFeedbackProps) {
  // One edit link below the issues, like on the EnVKV card.
  const editTab = getPkwEnvkvVehicleEditTab(props.issueCodes);
  const editAction: ButtonProps | undefined = isNotNil(editTab)
    ? {
        variant: 'link',
        size: 'small',
        leftIcon: 'image/edit',
        title: i18n.t('entity.vehicle.actions.goToEditVehicle'),
        to: composePath(vehiclesRoutes.editV2Tab, {params: {id: props.vehicleId, tab: editTab}}),
        onClick: props.onNavigate,
      }
    : undefined;
  const issueLabels = getPkwEnvkvIssueLabels(props.issueCodes).join(', ');
  const requirementReason = getPkwEnvkvRequirementReasonLabel(props.requirementReasonCodes ?? []);
  const cardAction: ButtonProps | undefined =
    props.issueCodes.some(isPkwEnvkvCalculatedValueIssue) || isNotNil(requirementReason)
      ? {
          variant: 'link',
          size: 'small',
          title: i18n.t('page.pkwEnvkv.actions.openCard'),
          to: `${composePath(vehiclesRoutes.detail, {params: {id: props.vehicleId}})}#pkw-envkv`,
          onClick: props.onNavigate,
        }
      : undefined;

  return (
    <Alert
      type={props.type ?? 'inline'}
      layout="detailed"
      variant={props.variant === 'blocked' ? 'error' : 'warning'}
      title={i18n.t(
        props.variant === 'blocked'
          ? 'page.pkwEnvkv.advertising.blocked'
          : 'page.pkwEnvkv.advertising.incompleteDocument'
      )}
      message={isNil(requirementReason) ? issueLabels : [issueLabels, requirementReason]}
      actions={reject(isNil, [editAction, cardAction])}
    />
  );
}
